/**
 * CameraService - Uses ACTUAL preview dimensions from plugin
 *
 * The plugin returns actual preview rect: {x, y, width, height}
 * We use this to correctly map screen coordinates to image coordinates.
 */

import { Capacitor } from '@capacitor/core';
import { CameraPreview } from '@capgo/camera-preview';

const STATE = {
  IDLE: 'idle',
  READY: 'ready',
  CAPTURING: 'capturing',
};

class CameraServiceClass {
  constructor() {
    this.state = STATE.IDLE;
    this.isNative = Capacitor.isNativePlatform();

    // Screen dimensions
    this.screenWidth = 0;
    this.screenHeight = 0;

    // ACTUAL preview rect returned by plugin
    this.previewX = 0;
    this.previewY = 0;
    this.previewWidth = 0;
    this.previewHeight = 0;

  }

  getState() { return this.state; }

  /**
   * HARD RESET: Always stop first, then start fresh
   */
  async show(parentElement = 'scanner-camera-view') {
    console.log('[CameraService] show() - HARD RESET');

    if (!this.isNative) {
      this.state = STATE.READY;
      return;
    }

    // Store screen dimensions
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;

    // ALWAYS STOP FIRST
    console.log('[CameraService] Stopping any existing session...');
    try {
      await CameraPreview.stop();
    } catch (e) {
      console.log('[CameraService] Stop result:', e?.message || 'OK');
    }

    // Delay for native cleanup
    await new Promise(r => setTimeout(r, 150));

    // START FRESH with full-screen request
    // Use 'center' positioning so the 4:3 preview is centered vertically
    // (black bars equal on top and bottom instead of all at bottom)
    const options = {
      position: 'rear',
      toBack: true,
      parent: parentElement,
      x: 0,
      y: 0,
      width: window.screen.width,
      height: window.screen.height,
      positioning: 'center',  // Center the letterboxed preview
      disableAudio: true,
      enableHighResolution: true,
      storeToFile: true,
      enableZoom: true,
      tapToFocus: true,
      focusMode: 'auto',
    };

    console.log('[CameraService] Starting with:', options);
    const startTime = Date.now();

    try {
      // Plugin returns actual preview rect!
      const result = await CameraPreview.start(options);
      const elapsed = Date.now() - startTime;
      console.log(`[CameraService] Plugin started in ${elapsed}ms`);
      console.log('[CameraService] Plugin returned preview rect:', result);

      // Store actual preview dimensions
      if (result && typeof result.x !== 'undefined') {
        this.previewX = result.x;
        this.previewY = result.y;
        this.previewWidth = result.width;
        this.previewHeight = result.height;
      } else {
        // Fallback to requested dimensions
        this.previewX = 0;
        this.previewY = 0;
        this.previewWidth = this.screenWidth;
        this.previewHeight = this.screenHeight;
      }

      console.log('[CameraService] ACTUAL preview:', {
        x: this.previewX,
        y: this.previewY,
        width: this.previewWidth,
        height: this.previewHeight,
      });

      this.state = STATE.READY;
      console.log('[CameraService] READY');

    } catch (err) {
      console.error('[CameraService] Start error:', err);
      throw err;
    }
  }

  /**
   * Stop camera
   */
  async hide() {
    console.log('[CameraService] hide()');
    if (!this.isNative) return;

    try {
      await CameraPreview.stop();
    } catch (e) {
      console.log('[CameraService] Stop error:', e?.message);
    }

    this.state = STATE.IDLE;
  }

  /**
   * Map screen tap to sensor coordinates (for focus)
   */
  mapScreenToSensor(screenX, screenY) {
    // Convert screen coords to preview-relative coords
    const previewRelX = screenX - this.previewX;
    const previewRelY = screenY - this.previewY;

    // Normalize to 0-1
    const sensorX = previewRelX / this.previewWidth;
    const sensorY = previewRelY / this.previewHeight;

    return {
      x: Math.max(0, Math.min(1, sensorX)),
      y: Math.max(0, Math.min(1, sensorY)),
    };
  }

  /**
   * Get crop rectangle - uses ACTUAL preview dimensions
   *
   * Frame rect is in screen coordinates.
   * We need to convert to image coordinates based on where the preview actually is.
   */
  getCropRect(frameRect, imageWidth, imageHeight) {
    console.log('[CameraService] === CROP CALCULATION ===');
    console.log(`  Screen: ${this.screenWidth}x${this.screenHeight}`);
    console.log(`  Preview: ${this.previewWidth}x${this.previewHeight} at (${this.previewX}, ${this.previewY})`);
    console.log(`  Image: ${imageWidth}x${imageHeight}`);
    console.log(`  Frame: (${frameRect.x.toFixed(0)}, ${frameRect.y.toFixed(0)}) ${frameRect.width.toFixed(0)}x${frameRect.height.toFixed(0)}`);

    // Convert frame from screen coords to preview-relative coords
    const frameRelX = frameRect.x - this.previewX;
    const frameRelY = frameRect.y - this.previewY;

    console.log(`  Frame relative to preview: (${frameRelX.toFixed(0)}, ${frameRelY.toFixed(0)})`);

    // Scale from preview coords to image coords
    const scaleX = imageWidth / this.previewWidth;
    const scaleY = imageHeight / this.previewHeight;

    console.log(`  Scale: ${scaleX.toFixed(2)}x, ${scaleY.toFixed(2)}y`);

    // Calculate crop rect in image coordinates
    const cropX = frameRelX * scaleX;
    const cropY = frameRelY * scaleY;
    const cropW = frameRect.width * scaleX;
    const cropH = frameRect.height * scaleY;

    // Clamp to image bounds
    const result = {
      x: Math.max(0, Math.round(cropX)),
      y: Math.max(0, Math.round(cropY)),
      width: Math.round(cropW),
      height: Math.round(cropH),
    };

    // Ensure we don't exceed image bounds
    if (result.x + result.width > imageWidth) {
      result.width = imageWidth - result.x;
    }
    if (result.y + result.height > imageHeight) {
      result.height = imageHeight - result.y;
    }

    console.log(`  RESULT: (${result.x}, ${result.y}) ${result.width}x${result.height}`);

    return result;
  }

  /**
   * Focus at screen point
   */
  async focusAtPoint(screenX, screenY) {
    if (this.state !== STATE.READY) return;

    const { x, y } = this.mapScreenToSensor(screenX, screenY);
    console.log(`[FOCUS] Screen (${screenX.toFixed(0)}, ${screenY.toFixed(0)}) -> Sensor (${x.toFixed(3)}, ${y.toFixed(3)})`);

    try {
      await CameraPreview.setFocus({ x, y });
    } catch (err) {
      console.log('[FOCUS] Error:', err?.message);
    }
  }

  /**
   * Capture photo
   */
  async capture() {
    if (this.state !== STATE.READY) {
      throw new Error('Camera not ready');
    }

    this.state = STATE.CAPTURING;

    try {
      const result = await CameraPreview.capture({ quality: 95 });
      this.state = STATE.READY;
      return { filePath: result.value };
    } catch (err) {
      this.state = STATE.READY;
      throw err;
    }
  }

  /**
   * Initialize - no-op, let show() do everything
   */
  async initialize() {
    console.log('[CameraService] initialize() - waiting for show()');
  }

  isReady() { return this.state === STATE.READY; }

  /**
   * Capture a low-res sample frame for detection (best-effort, no throws)
   */
  async captureSample(quality = 25) {
    if (this.state !== STATE.READY || !this.isNative) return null;
    try {
      const result = await CameraPreview.captureSample({ quality });
      return result.value || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Get the actual preview rect (for UI positioning)
   */
  getPreviewRect() {
    return {
      x: this.previewX,
      y: this.previewY,
      width: this.previewWidth,
      height: this.previewHeight,
    };
  }
}

const CameraService = new CameraServiceClass();
export default CameraService;
